import React, {
  ChangeEvent,
  FC,
  FocusEvent,
  KeyboardEvent,
  RefObject,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { selectSpaceName } from 'data/templates/projects/selectors';
import { useTranslation } from 'hooks/use-translation';
import { SpaceNameStyle, TextArea, TitleWrapper, Wrapper } from './styles';
import { useTitleStateChange } from './use-title-state-change';

export const DocumentHead: FC<{
  className?: string;
  refWrapper?: RefObject<HTMLDivElement> | null;
  readOnly?: boolean;
  title: string | null;
  onBlur?(event: FocusEvent<HTMLTextAreaElement>): void;
  onChange: (title: string) => void;
  onEnter?(event: KeyboardEvent<HTMLTextAreaElement>): void;
  onTitleViewStateChange?: ({ isVisible }: { isVisible: boolean }) => void;
  documentMaxWidth?: boolean;
  isResponsive?: boolean;
  isVersionsEditorEnabled?: boolean;
}> = ({
  className = '',
  readOnly,
  refWrapper,
  title,
  onChange,
  onBlur,
  onEnter,
  onTitleViewStateChange,
  documentMaxWidth,
  isResponsive,
  isVersionsEditorEnabled,
}) => {
  const documentTitlePlaceholder = useTranslation('editor.title_placeholder');
  const refTitle = useRef<HTMLDivElement | null>(null);

  useTitleStateChange(refTitle, refWrapper, onTitleViewStateChange);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) =>
    onChange(e.target.value);

  const handleEnter = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();

    if (onEnter) {
      onEnter(e);
    } else {
      e.currentTarget.blur();
    }
  };

  const { pathname } = useLocation();
  const isProjectTemplate = pathname.includes('templates/projects');

  const { spaceId } = useParams<{ spaceId: string }>();
  const spaceName = useSelector(selectSpaceName(Number(spaceId)));

  return (
    <Wrapper className={className} isTemplate={isProjectTemplate}>
      <TitleWrapper
        ref={refTitle}
        maxWidth={documentMaxWidth!}
        isResponsive={isResponsive}
        isVersionEditorHeader={isVersionsEditorEnabled}
      >
        {isProjectTemplate && <SpaceNameStyle>{spaceName}/</SpaceNameStyle>}
        {title !== null ? (
          <TextArea
            className="rl_document__title"
            autoSize={{ minRows: 1, maxRows: 6 }}
            placeholder={documentTitlePlaceholder}
            data-test-id="document.title.input"
            data-cy="document.title.input"
            readOnly={readOnly}
            value={title}
            onChange={handleChange}
            onBlur={onBlur}
            onPressEnter={handleEnter}
          />
        ) : null}
      </TitleWrapper>
    </Wrapper>
  );
};
